import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  Camera,
  Gavel,
  Gift,
  Landmark,
  LayoutDashboard,
  Minus,
  Package,
  Receipt,
  Search,
  Sparkles,
  Square,
  Store,
  Ticket,
  X,
} from "lucide-react";
import { api } from "../lib/api";
import { fmtDate, rm } from "../lib/format";
import { getSocket } from "../lib/socket";
import { Badge, Button, PageLoader, SearchSelect, statusColor, Toasts } from "../components/ui";
import { Icon } from "../components/icons";
import { DataTable, type Column } from "../components/DataTable";

const STOREFRONT_PATH = "__storefront__";

const STOREFRONT_URL = (import.meta.env.VITE_FRONTEND_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";
const STORAGE_KEY = "gaole_os_layout";
const MOBILE_MAX = 768;
const SNAP_EDGE_PX = 48;
const MIN_WINDOW_W = 320;
const MIN_WINDOW_H = 240;
const ICON_SIZE = 80;

interface DesktopBounds {
  width: number;
  height: number;
  left: number;
  top: number;
}

function getDesktopBounds(container: HTMLElement | null): DesktopBounds {
  if (!container) {
    const headerHeight = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
    ) || 48;
    return {
      width: globalThis.window.innerWidth,
      height: globalThis.window.innerHeight - headerHeight - 80,
      left: 0,
      top: headerHeight,
    };
  }
  const rect = container.getBoundingClientRect();
  return { width: rect.width, height: rect.height, left: rect.left, top: rect.top };
}

function clampWindowRect(
  x: number,
  y: number,
  w: number,
  h: number,
  bounds: DesktopBounds,
): { x: number; y: number; w: number; h: number } {
  const width = Math.min(Math.max(MIN_WINDOW_W, w), bounds.width);
  const height = Math.min(Math.max(MIN_WINDOW_H, h), bounds.height);
  return {
    x: Math.max(0, Math.min(x, bounds.width - width)),
    y: Math.max(0, Math.min(y, bounds.height - height)),
    w: width,
    h: height,
  };
}

function clampIconPos(x: number, y: number, bounds: DesktopBounds) {
  return {
    x: Math.max(0, Math.min(x, bounds.width - ICON_SIZE)),
    y: Math.max(0, Math.min(y, bounds.height - ICON_SIZE)),
  };
}

function getSnapRects(width: number, height: number) {
  const halfW = Math.floor(width / 2);
  const halfH = Math.floor(height / 2);
  return {
    left: { x: 0, y: 0, w: halfW, h: height },
    right: { x: halfW, y: 0, w: halfW, h: height },
    top: { x: 0, y: 0, w: width, h: halfH },
    bottom: { x: 0, y: halfH, w: width, h: halfH },
    tl: { x: 0, y: 0, w: halfW, h: halfH },
    tr: { x: halfW, y: 0, w: halfW, h: halfH },
    bl: { x: 0, y: halfH, w: halfW, h: halfH },
    br: { x: halfW, y: halfH, w: halfW, h: halfH },
  } as const;
}

type SnapMode = "left" | "right" | "tl" | "tr" | "bl" | "br";
type SnapModeExtended = SnapMode | "top" | "bottom";

function detectSnapZone(clientX: number, clientY: number, bounds: DesktopBounds): SnapMode | null {
  const cx = clientX - bounds.left;
  const cy = clientY - bounds.top;
  const nearLeft = cx < SNAP_EDGE_PX;
  const nearRight = cx > bounds.width - SNAP_EDGE_PX;
  const nearTop = cy < SNAP_EDGE_PX;
  const nearBottom = cy > bounds.height - SNAP_EDGE_PX;

  if (nearLeft && nearTop) return "tl";
  if (nearRight && nearTop) return "tr";
  if (nearLeft && nearBottom) return "bl";
  if (nearRight && nearBottom) return "br";
  if (nearLeft) return "left";
  if (nearRight) return "right";
  return null;
}

const LazyDashboard = lazy(() => import("./Dashboard").then((m) => ({ default: m.Dashboard })));
const LazyCardUploader = lazy(() => import("./CardUploader").then((m) => ({ default: m.CardUploader })));
const LazyOrders = lazy(() => import("./Orders").then((m) => ({ default: m.Orders })));
const LazyFulfilment = lazy(() => import("./Fulfilment").then((m) => ({ default: m.Fulfilment })));
const LazyPacks = lazy(() => import("./Packs").then((m) => ({ default: m.Packs })));
const LazyAuctions = lazy(() => import("./Auctions").then((m) => ({ default: m.Auctions })));

interface DesktopApp {
  id: string;
  title: string;
  path: string;
  icon: LucideIcon;
  defaultX: number;
  defaultY: number;
}

const APPS: DesktopApp[] = [
  { id: "dashboard", title: "Dashboard", path: "/", icon: LayoutDashboard, defaultX: 48, defaultY: 72 },
  { id: "storefront", title: "Storefront", path: STOREFRONT_PATH, icon: Store, defaultX: 168, defaultY: 72 },
  { id: "raffles", title: "Raffles", path: "/raffles", icon: Ticket, defaultX: 288, defaultY: 72 },
  { id: "receipts", title: "Receipts", path: "/receipts", icon: Landmark, defaultX: 408, defaultY: 72 },
  { id: "orders", title: "Orders", path: "/orders", icon: Receipt, defaultX: 528, defaultY: 72 },
  { id: "card-uploader", title: "Card Uploader", path: "/card-uploader", icon: Camera, defaultX: 48, defaultY: 192 },
  { id: "fulfilment", title: "Fulfilment", path: "/fulfilment", icon: Package, defaultX: 168, defaultY: 192 },
  { id: "packs", title: "Packs", path: "/packs", icon: Gift, defaultX: 288, defaultY: 192 },
  { id: "auctions", title: "Auctions", path: "/auctions", icon: Gavel, defaultX: 408, defaultY: 192 },
];

interface OsWindow {
  id: string;
  title: string;
  path: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  minimized: boolean;
  maximized: boolean;
}

interface StoredLayout {
  icons: Record<string, { x: number; y: number }>;
  windows: OsWindow[];
  nextZ: number;
}

function loadLayout(): StoredLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as StoredLayout;
  } catch {
    /* ignore */
  }
  const icons: Record<string, { x: number; y: number }> = {};
  for (const app of APPS) icons[app.id] = { x: app.defaultX, y: app.defaultY };
  return { icons, windows: [], nextZ: 10 };
}

function normalizeLayout(layout: StoredLayout, bounds: DesktopBounds): StoredLayout {
  const icons = { ...layout.icons };
  for (const [id, pos] of Object.entries(icons)) {
    icons[id] = clampIconPos(pos.x, pos.y, bounds);
  }
  const windows = layout.windows.map((w) => {
    if (w.maximized) return w;
    const clamped = clampWindowRect(w.x, w.y, w.w, w.h, bounds);
    return { ...w, ...clamped };
  });
  return { ...layout, icons, windows };
}

function saveLayout(layout: StoredLayout): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
}

function OpenFullPage({ path, title }: { path: string; title: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-sm text-white/55">{title} works best in the full backoffice layout.</p>
      <Button size="sm" onClick={() => navigate(path)}>
        Open full page
      </Button>
    </div>
  );
}

interface ReceiptRow {
  id: string;
  orderNo: string;
  customer: string;
  amount: number;
  status: string;
  createdAt: string;
}

function OsReceiptsMini() {
  const { data, isLoading } = useQuery({
    queryKey: ["os", "receipts"],
    queryFn: () =>
      api<{ items: ReceiptRow[] }>("/payments/receipts?status=PENDING").then((r) => r.items),
    refetchInterval: 10_000,
  });

  const columns: Column<ReceiptRow>[] = [
    {
      key: "order",
      header: "Order",
      sortable: true,
      sortValue: (r) => r.orderNo,
      render: (r) => <span className="font-mono text-xs">{r.orderNo}</span>,
    },
    {
      key: "customer",
      header: "Customer",
      sortable: true,
      sortValue: (r) => r.customer,
      render: (r) => r.customer,
    },
    {
      key: "amount",
      header: "Amount",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.amount,
      exportValue: (r) => r.amount,
      render: (r) => rm(r.amount),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
  ];

  return (
    <div className="h-full overflow-auto p-3">
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(r) => r.id}
        loading={isLoading}
        exportFilename="os-pending-receipts"
        empty={{ title: "No pending receipts", hint: "Bank-in uploads appear here." }}
      />
    </div>
  );
}

function OsRaffleMonitor() {
  const [searchParams] = useSearchParams();
  const paramRaffle = searchParams.get("raffle") ?? "";
  const [raffleId, setRaffleId] = useState<string>(paramRaffle);

  const { data: raffles, isLoading: rafflesLoading, isError } = useQuery({
    queryKey: ["os", "raffles"],
    queryFn: () =>
      api<{ items: { id: string; title: string; status: string }[] }>("/raffles?all=true").then((r) => r.items),
    refetchInterval: 10_000,
  });

  const openRaffles = useMemo(() => {
    const list = raffles ?? [];
    const live = list.filter((r) => ["OPEN", "SCHEDULED", "DRAWING", "DRAWN"].includes(r.status));
    return live.length ? live : list;
  }, [raffles]);

  const raffleSelectOptions = useMemo(
    () => openRaffles.map((r) => ({ value: r.id, label: `${r.title} (${r.status})` })),
    [openRaffles],
  );

  const rafflePlaceholder = rafflesLoading
    ? "Loading…"
    : isError
      ? "Failed to load"
      : "Select raffle…";

  useEffect(() => {
    if (paramRaffle) setRaffleId(paramRaffle);
  }, [paramRaffle]);

  useEffect(() => {
    if (!raffleId && openRaffles[0]) setRaffleId(openRaffles[0].id);
  }, [openRaffles, raffleId]);

  const { data: monitor } = useQuery({
    queryKey: ["os", "raffle-monitor", raffleId],
    queryFn: () =>
      api<{
        raffle: { title: string; status: string };
        ticketCount: number;
        maxTicketNo?: number;
        viewers: number;
        tickets: { ticketNo: number; name: string; at: string; forfeited: boolean }[];
      }>(`/raffles/${raffleId}/monitor`),
    enabled: !!raffleId,
    refetchInterval: 3000,
  });

  useEffect(() => {
    if (!raffleId) return;
    const socket = getSocket();
    socket.emit("raffle:monitor", raffleId);
    const onJoin = () => void 0;
    socket.on("raffle:presence:joined", onJoin);
    socket.on("raffle:presence:left", onJoin);
    return () => {
      socket.off("raffle:presence:joined", onJoin);
      socket.off("raffle:presence:left", onJoin);
    };
  }, [raffleId]);

  const ticketColumns: Column<{ ticketNo: number; name: string; at: string; forfeited: boolean }>[] = [
    {
      key: "no",
      header: "#",
      sortable: true,
      sortValue: (r) => r.ticketNo,
      render: (r) => r.ticketNo,
    },
    {
      key: "name",
      header: "Player",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => r.name,
    },
    {
      key: "at",
      header: "Joined",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.at,
      exportValue: (r) => fmtDate(r.at),
      render: (r) => fmtDate(r.at),
    },
  ];

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-3">
      <SearchSelect
        label="Live raffle"
        placeholder={rafflePlaceholder}
        options={raffleSelectOptions}
        value={raffleId}
        onChange={setRaffleId}
        disabled={rafflesLoading || isError}
      />
      {!rafflesLoading && !isError && openRaffles.length === 0 && (
        <p className="text-xs text-white/45">No raffles yet — create one under Raffles.</p>
      )}
      {monitor && (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-white/5 p-2">
              <p className="text-[10px] uppercase text-white/40">Entries</p>
              <p className="font-display text-lg font-bold tabular-nums">{monitor.ticketCount}</p>
              {monitor.maxTicketNo != null && monitor.maxTicketNo > monitor.ticketCount && (
                <p className="mt-0.5 text-[9px] leading-snug text-amber-300/80">
                  #{monitor.maxTicketNo} highest · gaps from failed joins
                </p>
              )}
            </div>
            <div className="rounded-xl bg-white/5 p-2">
              <p className="text-[10px] uppercase text-white/40">Viewers</p>
              <p className="font-display text-lg font-bold tabular-nums">{monitor.viewers}</p>
            </div>
            <div className="rounded-xl bg-white/5 p-2">
              <p className="text-[10px] uppercase text-white/40">Status</p>
              <Badge color={statusColor(monitor.raffle.status)}>{monitor.raffle.status}</Badge>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable
              columns={ticketColumns}
              rows={monitor.tickets}
              rowKey={(r) => String(r.ticketNo)}
              exportFilename="os-raffle-tickets"
            />
          </div>
        </>
      )}
    </div>
  );
}

function OsStorefront() {
  return (
    <iframe
      title="Storefront"
      src={STOREFRONT_URL}
      className="h-full w-full border-0 bg-brand-dark"
      allow="payment *; clipboard-write"
    />
  );
}

function WindowBody({ path }: { path: string }) {
  const app = APPS.find((a) => a.path === path);
  const title = app?.title ?? "App";

  if (path === STOREFRONT_PATH) {
    return (
      <div className="h-full min-h-0 overflow-hidden">
        <OsStorefront />
      </div>
    );
  }

  const body = (() => {
    switch (path) {
      case "/":
        return <LazyDashboard />;
      case "/raffles":
        return <OsRaffleMonitor />;
      case "/receipts":
        return <OsReceiptsMini />;
      case "/orders":
        return <LazyOrders />;
      case "/card-uploader":
        return <LazyCardUploader />;
      case "/fulfilment":
        return <LazyFulfilment />;
      case "/packs":
        return <LazyPacks />;
      case "/auctions":
        return <LazyAuctions />;
      default:
        return <OpenFullPage path={path} title={title} />;
    }
  })();

  return (
    <div className="h-full min-h-0 overflow-auto">
      <Suspense fallback={<PageLoader />}>{body}</Suspense>
    </div>
  );
}

type DragKind =
  | { type: "icon"; id: string; ox: number; oy: number }
  | { type: "window"; id: string; ox: number; oy: number }
  | { type: "resize"; id: string; ow: number; oh: number; sx: number; sy: number };

export function OsMode() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobile, setMobile] = useState(() => globalThis.window.innerWidth < MOBILE_MAX);
  const [layout, setLayout] = useState<StoredLayout>(loadLayout);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [snapPreview, setSnapPreview] = useState<SnapMode | null>(null);
  const dragRef = useRef<DragKind | null>(null);
  const desktopRef = useRef<HTMLDivElement>(null);
  const bootstrappedOps = useRef(false);
  const normalizedRef = useRef(false);

  useEffect(() => {
    if (normalizedRef.current || !desktopRef.current) return;
    normalizedRef.current = true;
    const bounds = getDesktopBounds(desktopRef.current);
    setLayout((prev) => normalizeLayout(prev, bounds));
  }, []);

  useEffect(() => {
    const onResize = () => {
      setMobile(globalThis.window.innerWidth < MOBILE_MAX);
      const bounds = getDesktopBounds(desktopRef.current);
      setLayout((prev) => normalizeLayout(prev, bounds));
    };
    globalThis.window.addEventListener("resize", onResize);
    return () => globalThis.window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const blockContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu, { capture: true });
    return () => document.removeEventListener("contextmenu", blockContextMenu, { capture: true });
  }, []);

  useEffect(() => {
    saveLayout(layout);
  }, [layout]);

  // Ops console: when opened with ?raffle=, auto-open Raffles + Receipts side by side
  useEffect(() => {
    if (bootstrappedOps.current) return;
    if (!searchParams.get("raffle")) return;
    bootstrappedOps.current = true;
    const rafflesApp = APPS.find((a) => a.id === "raffles");
    const receiptsApp = APPS.find((a) => a.id === "receipts");
    if (!rafflesApp || !receiptsApp) return;
    setLayout((prev) => {
      let nextZ = prev.nextZ;
      const windows = [...prev.windows];
      const ensure = (app: DesktopApp, x: number, y: number) => {
        if (windows.some((w) => w.path === app.path)) return;
        nextZ += 1;
        windows.push({
          id: `win-${app.id}-${Date.now()}`,
          title: app.title,
          path: app.path,
          x,
          y,
          w: 640,
          h: 520,
          z: nextZ,
          minimized: false,
          maximized: false,
        });
      };
      ensure(rafflesApp, 40, 60);
      ensure(receiptsApp, 700, 60);
      return { ...prev, windows, nextZ };
    });
  }, [searchParams]);

  const openWindows = layout.windows.filter((w) => !w.minimized);
  const minimizedWindows = layout.windows.filter((w) => w.minimized);

  const focusWindow = useCallback((id: string) => {
    setLayout((prev) => {
      const nextZ = prev.nextZ + 1;
      return {
        ...prev,
        nextZ,
        windows: prev.windows.map((w) => (w.id === id ? { ...w, z: nextZ, minimized: false } : w)),
      };
    });
    setFocusedId(id);
  }, []);

  const openApp = useCallback(
    (app: DesktopApp) => {
      setLayout((prev) => {
        const existing = prev.windows.find((w) => w.path === app.path);
        if (existing) {
          const nextZ = prev.nextZ + 1;
          setFocusedId(existing.id);
          return {
            ...prev,
            nextZ,
            windows: prev.windows.map((w) =>
              w.id === existing.id ? { ...w, z: nextZ, minimized: false } : w,
            ),
          };
        }
        const id = `win-${app.id}-${Date.now()}`;
        const nextZ = prev.nextZ + 1;
        setFocusedId(id);
        const bounds = getDesktopBounds(desktopRef.current);
        const { x, y } = clampWindowRect(
          120 + prev.windows.length * 24,
          80 + prev.windows.length * 24,
          720,
          480,
          bounds,
        );
        return {
          ...prev,
          nextZ,
          windows: [
            ...prev.windows,
            {
              id,
              title: app.title,
              path: app.path,
              x,
              y,
              w: 720,
              h: 480,
              z: nextZ,
              minimized: false,
              maximized: false,
            },
          ],
        };
      });
    },
    [],
  );

  const closeWindow = useCallback((id: string) => {
    setLayout((prev) => ({ ...prev, windows: prev.windows.filter((w) => w.id !== id) }));
    setFocusedId((f) => (f === id ? null : f));
  }, []);

  const toggleMinimize = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      windows: prev.windows.map((w) => (w.id === id ? { ...w, minimized: !w.minimized } : w)),
    }));
  }, []);

  const toggleMaximize = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      windows: prev.windows.map((w) => (w.id === id ? { ...w, maximized: !w.maximized } : w)),
    }));
    setFocusedId(id);
  }, []);

  const snapWindow = useCallback((windowId: string, mode: SnapModeExtended) => {
    const bounds = getDesktopBounds(desktopRef.current);
    const r = getSnapRects(bounds.width, bounds.height)[mode];
    setLayout((prev) => ({
      ...prev,
      windows: prev.windows.map((w) =>
        w.id === windowId ? { ...w, ...r, maximized: false, minimized: false } : w,
      ),
    }));
  }, []);

  const snapFocused = useCallback(
    (mode: SnapModeExtended) => {
      if (!focusedId) return;
      snapWindow(focusedId, mode);
    },
    [focusedId, snapWindow],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable) return;

      // Shift+M exits OS mode (toggle)
      if (e.shiftKey && e.key.toLowerCase() === "m") {
        e.preventDefault();
        navigate("/");
        return;
      }
      // Shift+W close focused
      if (e.shiftKey && e.key.toLowerCase() === "w" && focusedId) {
        e.preventDefault();
        closeWindow(focusedId);
        return;
      }
      // Shift+X close all
      if (e.shiftKey && e.key.toLowerCase() === "x") {
        e.preventDefault();
        setLayout((prev) => ({ ...prev, windows: [] }));
        setFocusedId(null);
        return;
      }
      // Half-screen snaps
      if (e.shiftKey && !e.altKey) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          snapFocused("left");
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          snapFocused("right");
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          snapFocused("top");
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          snapFocused("bottom");
        }
      }
      // Quad snaps Shift+Alt+1..4
      if (e.shiftKey && e.altKey) {
        if (e.key === "1") {
          e.preventDefault();
          snapFocused("tl");
        } else if (e.key === "2") {
          e.preventDefault();
          snapFocused("tr");
        } else if (e.key === "3") {
          e.preventDefault();
          snapFocused("bl");
        } else if (e.key === "4") {
          e.preventDefault();
          snapFocused("br");
        }
      }
    };
    globalThis.window.addEventListener("keydown", onKey);
    return () => globalThis.window.removeEventListener("keydown", onKey);
  }, [focusedId, closeWindow, navigate, snapFocused]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (drag.type === "icon") {
        const bounds = getDesktopBounds(desktopRef.current);
        const pos = clampIconPos(
          e.clientX - bounds.left - drag.ox,
          e.clientY - bounds.top - drag.oy,
          bounds,
        );
        setLayout((prev) => ({
          ...prev,
          icons: {
            ...prev.icons,
            [drag.id]: pos,
          },
        }));
      } else if (drag.type === "window") {
        const bounds = getDesktopBounds(desktopRef.current);
        setSnapPreview(detectSnapZone(e.clientX, e.clientY, bounds));
        setLayout((prev) => ({
          ...prev,
          windows: prev.windows.map((w) => {
            if (w.id !== drag.id) return w;
            const clamped = clampWindowRect(
              e.clientX - bounds.left - drag.ox,
              e.clientY - bounds.top - drag.oy,
              w.w,
              w.h,
              bounds,
            );
            return { ...w, ...clamped, maximized: false };
          }),
        }));
      } else if (drag.type === "resize") {
        const bounds = getDesktopBounds(desktopRef.current);
        setLayout((prev) => ({
          ...prev,
          windows: prev.windows.map((w) => {
            if (w.id !== drag.id) return w;
            const clamped = clampWindowRect(
              w.x,
              w.y,
              drag.ow + (e.clientX - drag.sx),
              drag.oh + (e.clientY - drag.sy),
              bounds,
            );
            return { ...w, ...clamped, maximized: false };
          }),
        }));
      }
    };
    const onUp = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (drag?.type === "window") {
        const bounds = getDesktopBounds(desktopRef.current);
        const zone = detectSnapZone(e.clientX, e.clientY, bounds);
        if (zone) snapWindow(drag.id, zone);
      }
      dragRef.current = null;
      setSnapPreview(null);
    };
    globalThis.window.addEventListener("mousemove", onMove);
    globalThis.window.addEventListener("mouseup", onUp);
    return () => {
      globalThis.window.removeEventListener("mousemove", onMove);
      globalThis.window.removeEventListener("mouseup", onUp);
    };
  }, [snapWindow]);

  if (mobile) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-brand-dark p-6 text-center">
        <p className="font-display text-lg font-bold">OS Mode needs a larger screen</p>
        <p className="max-w-xs text-sm text-white/50">Use a desktop viewport (768px or wider) for multitasking windows.</p>
        <Button onClick={() => navigate("/")}>Exit</Button>
        <Toasts />
      </div>
    );
  }

  return (
    <div className="os-desktop relative flex h-full flex-col overflow-hidden">
      {/* Header */}
      <header className="glass relative z-50 flex h-12 shrink-0 items-center gap-3 border-b border-white/10 px-4">
        <img src="/logo-wordmark.png" alt="Mr Gaole" className="h-6 w-auto object-contain" />
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold tabular-nums">
          {layout.windows.length} window{layout.windows.length === 1 ? "" : "s"}
        </span>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => navigate("/")}>
          Exit
        </Button>
      </header>

      {/* Desktop surface */}
      <div ref={desktopRef} className="relative min-h-0 flex-1 overflow-hidden pb-20">
        {snapPreview && (() => {
          const bounds = getDesktopBounds(desktopRef.current);
          const rect = getSnapRects(bounds.width, bounds.height)[snapPreview];
          return (
            <div
              className="pointer-events-none absolute z-[100] rounded-xl border-2 border-brand-pink/60 bg-brand-pink/10 transition-all duration-150"
              style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
            />
          );
        })()}
        {/* Icons */}
        {APPS.map((app) => {
          const pos = layout.icons[app.id] ?? { x: app.defaultX, y: app.defaultY };
          return (
            <button
              key={app.id}
              type="button"
              style={{ left: pos.x, top: pos.y }}
              className="absolute flex w-20 flex-col items-center gap-1 rounded-xl p-2 text-center transition-transform hover:scale-105 active:scale-95"
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                const bounds = getDesktopBounds(desktopRef.current);
                dragRef.current = {
                  type: "icon",
                  id: app.id,
                  ox: e.clientX - bounds.left - pos.x,
                  oy: e.clientY - bounds.top - pos.y,
                };
              }}
              onClick={() => openApp(app)}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white/80 shadow-lg ring-1 ring-white/10">
                <Icon icon={app.icon} size="lg" />
              </span>
              <span className="text-[10px] font-medium leading-tight text-white/80">{app.title}</span>
            </button>
          );
        })}

        {/* Windows */}
        {layout.windows.map((win) => {
          if (win.minimized) return null;
          const style = win.maximized
            ? { left: 0, top: 0, width: "100%", height: "calc(100% - 0px)", zIndex: win.z }
            : { left: win.x, top: win.y, width: win.w, height: win.h, zIndex: win.z };

          return (
            <div
              key={win.id}
              className={`absolute flex flex-col overflow-hidden rounded-xl border border-white/10 bg-surface-2 shadow-2xl ${
                focusedId === win.id ? "ring-1 ring-brand-pink/40" : ""
              }`}
              style={style}
              onMouseDown={() => focusWindow(win.id)}
            >
              <div
                className="flex h-9 shrink-0 cursor-grab items-center gap-2 border-b border-white/10 bg-white/[0.04] px-2 active:cursor-grabbing"
                onMouseDown={(e: ReactMouseEvent) => {
                  if (win.maximized) return;
                  e.stopPropagation();
                  const bounds = getDesktopBounds(desktopRef.current);
                  dragRef.current = {
                    type: "window",
                    id: win.id,
                    ox: e.clientX - bounds.left - win.x,
                    oy: e.clientY - bounds.top - win.y,
                  };
                }}
                onDoubleClick={() => toggleMaximize(win.id)}
              >
                <span className="min-w-0 flex-1 truncate px-1 text-xs font-semibold">{win.title}</span>
                <button
                  type="button"
                  className="h-6 w-6 rounded text-white/50 hover:bg-white/10 hover:text-white"
                  onClick={() => toggleMinimize(win.id)}
                >
                  <Minus size={14} />
                </button>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-white/50 hover:bg-white/10 hover:text-white"
                  onClick={() => toggleMaximize(win.id)}
                >
                  <Square size={12} />
                </button>
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center rounded text-white/50 hover:bg-red-500/80 hover:text-white"
                  onClick={() => closeWindow(win.id)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden bg-surface">
                <WindowBody path={win.path} />
              </div>
              {!win.maximized && (
                <div
                  className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    dragRef.current = {
                      type: "resize",
                      id: win.id,
                      ow: win.w,
                      oh: win.h,
                      sx: e.clientX,
                      sy: e.clientY,
                    };
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Dock */}
      <footer className="pointer-events-none fixed inset-x-0 bottom-4 z-[5] flex justify-center px-4">
        <div className="glass pointer-events-auto flex max-w-full items-center gap-2 rounded-2xl px-3 py-2 shadow-2xl">
          <button type="button" className="rounded-xl p-2 text-white/70 transition-transform hover:scale-110" title="Search">
            <Search size={18} />
          </button>
          <button type="button" className="rounded-xl p-2 text-white/70 transition-transform hover:scale-110" title="AI">
            <Sparkles size={18} />
          </button>
          <div className="mx-1 h-6 w-px bg-white/15" />
          {[...openWindows, ...minimizedWindows].map((win) => (
            <button
              key={win.id}
              type="button"
              title={win.title}
              onClick={() => focusWindow(win.id)}
              className={`max-w-[120px] truncate rounded-xl px-3 py-1.5 text-xs font-semibold transition-transform hover:scale-105 ${
                focusedId === win.id && !win.minimized
                  ? "bg-gradient-brand text-white"
                  : "bg-white/10 text-white/70"
              }`}
            >
              {win.title}
            </button>
          ))}
        </div>
      </footer>

      <Toasts />
    </div>
  );
}
